import React, { useEffect, useState } from 'react';
import { Box, Button, Checkbox, FormControlLabel, Radio, TextField, Typography } from '@material-ui/core';
import { getNoteEmailUsers, updateNoteEmailRecipients } from '../../../actions/hubspot';
import { Toast } from 'components/shared/Toaster';
import { Input } from 'components/ds/Input';

const EMAIL_PATTERN = /^[^\s@,;]+@[^\s@,;]+\.[^\s@,;]{2,}$/;
// Longer user lists get a search box.
const USER_SEARCH_MIN = 7;

const OPTIONS = [
  { value: 'COMPANY', label: 'Company email', caption: 'One shared inbox gets every note email.' },
  { value: 'USERS', label: 'Selected users', caption: 'Each chosen user gets the email.' }
];

const toForm = (settings) => ({
  recipient: settings?.noteEmailRecipient || 'COMPANY',
  companyEmail: settings?.noteEmailCompanyEmail || '',
  users: (settings?.noteEmailUsers || []).map((e) => e.toLowerCase())
});

const sameUsers = (a, b) => a.length === b.length && a.every((e) => b.includes(e));

/**
 * Who gets the HubSpot note email (shown under the "Email when a HubSpot note is added" switch). One choice,
 * like the decline email's "Replies go to": a company inbox or chosen users. Nothing is sent until one is saved.
 */
const HubSpotNoteEmailRecipients = ({ settings, disabled, onSaved }) => {
  const [saved, setSaved] = useState(() => toForm(settings));
  const [form, setForm] = useState(() => toForm(settings));
  const [userOptions, setUserOptions] = useState([]);
  const [usersFailed, setUsersFailed] = useState(false);
  const [search, setSearch] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [companyTouched, setCompanyTouched] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getNoteEmailUsers()
      .then((res) => {
        const list = res?.response || [];
        setUserOptions([...list].sort((a, b) => (a.name || a.email).localeCompare(b.name || b.email)));
      })
      .catch(() => setUsersFailed(true));
  }, []);

  const companyEmail = form.companyEmail.trim();
  const companyInvalid = form.recipient === 'COMPANY' && !EMAIL_PATTERN.test(companyEmail)
    ? (companyEmail ? 'Enter a valid email address, such as credit@yourcompany.com.' : 'Enter a company email, or choose another option.')
    : null;
  // The company field shows its own error (after blur or a save attempt), like the decline email settings.
  const companyError = companyInvalid && (submitted || companyTouched) ? companyInvalid : null;
  const usersError = form.recipient === 'USERS' && form.users.length === 0
    ? 'Choose at least one user, or choose another option.'
    : null;
  const error = companyInvalid || usersError;
  const suggestedCompanyEmail = settings?.noteEmailSuggestedCompanyEmail;
  const showSuggestion = suggestedCompanyEmail && suggestedCompanyEmail.toLowerCase() !== companyEmail.toLowerCase();
  const clean = form.recipient === saved.recipient && companyEmail === saved.companyEmail && sameUsers(form.users, saved.users);

  const toggleUser = (email) => setForm((f) => ({
    ...f,
    users: f.users.includes(email) ? f.users.filter((e) => e !== email) : [...f.users, email]
  }));

  const handleSave = async () => {
    setSubmitted(true);
    if (error) return;
    setSaving(true);
    try {
      const res = await updateNoteEmailRecipients({ recipient: form.recipient, companyEmail, users: form.users });
      const next = toForm(res?.response);
      setSaved(next);
      setForm(next);
      setSubmitted(false);
      if (onSaved) onSaved(res?.response);
      Toast({ type: 'success', message: 'Note email recipients saved' });
    } catch {
      Toast({ type: 'error', message: 'Failed to save note email recipients' });
    } finally {
      setSaving(false);
    }
  };

  const query = search.trim().toLowerCase();
  const visibleUsers = query
    ? userOptions.filter((u) => `${u.name || ''} ${u.email}`.toLowerCase().includes(query))
    : userOptions;

  return (
    <Box mt={2} pt={2} style={{ borderTop: '1px solid #f0f0f0' }}>
      <Typography variant="caption" style={{ color: 'var(--fg-tertiary)' }}>Send the email to</Typography>
      {OPTIONS.map((o) => (
        <Box key={o.value} mt={1}>
          <FormControlLabel
            control={
              <Radio
                checked={form.recipient === o.value}
                onChange={() => setForm((f) => ({ ...f, recipient: o.value }))}
                color="primary"
                size="small"
                disabled={disabled || saving}
              />
            }
            label={<Typography variant="body2">{o.label}</Typography>}
          />
          <Typography variant="caption" component="div" style={{ color: 'var(--fg-tertiary)', marginLeft: 32, marginTop: -6 }}>
            {o.caption}
          </Typography>

          {o.value === 'COMPANY' && form.recipient === 'COMPANY' && (
            <Box mt={1} ml={4} style={{ display: 'flex', flexDirection: 'column', gap: 6, maxWidth: 446 }}>
              <Input
                id="hubspot-note-email-company"
                label="Company email"
                required
                type="email"
                placeholder="credit@yourcompany.com"
                value={form.companyEmail}
                onChange={(e) => {
                  // Read now: React 16 pools events, so e.target is null by the time the updater runs.
                  const companyEmail = e.target.value;
                  setForm((f) => ({ ...f, companyEmail }));
                }}
                onBlur={() => setCompanyTouched(true)}
                error={companyError}
                disabled={disabled || saving}
              />
              {showSuggestion && (
                <Box display="flex" alignItems="baseline" style={{ gap: 8, minWidth: 0, fontSize: 13 }}>
                  <button
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, companyEmail: suggestedCompanyEmail }))}
                    disabled={disabled || saving}
                    style={{ flex: 'none', padding: 0, border: 0, background: 'none', color: 'var(--fg-default)', font: 'inherit', fontWeight: 500, textDecoration: 'underline', cursor: 'pointer' }}
                  >
                    Use client email
                  </button>
                  <span style={{ overflow: 'hidden', color: 'var(--fg-tertiary)', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {suggestedCompanyEmail}
                  </span>
                </Box>
              )}
            </Box>
          )}

          {o.value === 'USERS' && form.recipient === 'USERS' && (
            <Box mt={1} ml={4}>
              {usersFailed ? (
                <Typography variant="caption" style={{ color: '#A3251A' }}>Couldn't load users. Reopen this dialog to try again.</Typography>
              ) : userOptions.length === 0 ? (
                <Typography variant="caption" style={{ color: 'var(--fg-tertiary)' }}>No active users to choose from.</Typography>
              ) : (
                <>
                  {userOptions.length >= USER_SEARCH_MIN && (
                    <TextField
                      size="small"
                      variant="outlined"
                      placeholder="Search users"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      style={{ width: 320, maxWidth: '100%', marginBottom: 8 }}
                    />
                  )}
                  <Box style={{ maxHeight: 220, overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: 8, padding: '4px 8px' }}>
                    {visibleUsers.map((u) => {
                      const email = u.email.toLowerCase();
                      return (
                        <Box key={email}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={form.users.includes(email)}
                                onChange={() => toggleUser(email)}
                                color="primary"
                                size="small"
                                disabled={disabled || saving}
                              />
                            }
                            label={
                              <Typography variant="body2">
                                {u.name || u.email}
                                {u.name && <span style={{ color: 'var(--fg-tertiary)' }}> · {u.email}</span>}
                              </Typography>
                            }
                          />
                        </Box>
                      );
                    })}
                  </Box>
                  {form.users.length > 0 && (
                    <Typography variant="caption" style={{ color: 'var(--fg-tertiary)' }}>
                      {form.users.length} {form.users.length === 1 ? 'user' : 'users'} selected
                    </Typography>
                  )}
                </>
              )}
            </Box>
          )}
        </Box>
      ))}

      {submitted && usersError && (
        <Typography variant="caption" component="div" style={{ color: '#A3251A', marginTop: 8 }}>{usersError}</Typography>
      )}
      <Box display="flex" justifyContent="flex-end" mt={2}>
        <Button
          variant="contained"
          color="primary"
          size="small"
          onClick={handleSave}
          disabled={disabled || saving || clean}
        >
          {saving ? 'Saving…' : 'Save recipients'}
        </Button>
      </Box>
    </Box>
  );
};

export default HubSpotNoteEmailRecipients;
